import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

export type BottleneckHeatmapTier = "hot" | "cold";
export type BottleneckHeatmapBucket = "day" | "week" | "month";

export interface BottleneckHeatmapRow {
  step: string;
  avgDurationMs: number;
  docCount: number;
  historicalAvgMs: number | null;
  ratio: number | null;
}

export interface BottleneckHeatmapResponse {
  tier: BottleneckHeatmapTier;
  bucket: BottleneckHeatmapBucket;
  rows: BottleneckHeatmapRow[];
}

export function useBottleneckHeatmap(
  from?: string,
  to?: string,
  bucket?: BottleneckHeatmapBucket,
) {
  const [data, setData] = useState<BottleneckHeatmapResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (bucket) params.set("bucket", bucket);
      const query = params.toString() ? `?${params.toString()}` : "";
      const result = await api.get<BottleneckHeatmapResponse>(`/analytics/bottleneck-heatmap${query}`);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load bottleneck heatmap");
    } finally {
      setIsLoading(false);
    }
  }, [from, to, bucket]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
